<script setup>
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import CustomMenuModal from './component/CustomMenuModal.vue';
import CustomMenuTableBody from './component/CustomMenuTableBody.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const store = useStore();
const { t } = useI18n();

const showCustomMenuModal = ref(false);
const customMenuModalMode = ref('add');
const selectedMenu = ref(null);
const loading = ref({});
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});

const records = useMapGetter('customMenu/getCustomMenus');
const uiFlags = useMapGetter('customMenu/getUIFlags');

const deleteConfirmText = computed(
  () => `${t('CUSTOM_MENU.DELETE.CONFIRM.YES')} ${activeResponse.value.name}`
);

const deleteRejectText = computed(
  () => `${t('CUSTOM_MENU.DELETE.CONFIRM.NO')} ${activeResponse.value.name}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.name} ? `;
});

const fetchCustomMenus = async () => {
  try {
    await store.dispatch('customMenu/getCustomMenus');
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCustomMenus();
});

const tableHeaders = computed(() => {
  return [
    t('CUSTOM_MENU.LIST.TABLE_HEADER.NAME'),
    t('CUSTOM_MENU.LIST.TABLE_HEADER.URL'),
    t('CUSTOM_MENU.LIST.TABLE_HEADER.STATUS'),
    t('CUSTOM_MENU.LIST.TABLE_HEADER.ACTIONS'),
  ];
});

const showAlertMessage = message => {
  loading.value[activeResponse.value.id] = false;
  activeResponse.value = {};
  useAlert(message);
};

const openAddModal = () => {
  customMenuModalMode.value = 'add';
  selectedMenu.value = null;
  showCustomMenuModal.value = true;
};

const openEditModal = menu => {
  customMenuModalMode.value = 'edit';
  selectedMenu.value = menu;
  showCustomMenuModal.value = true;
};

const hideCustomMenuModal = () => {
  selectedMenu.value = null;
  showCustomMenuModal.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCustomMenu = async id => {
  try {
    await store.dispatch('customMenu/deleteCustomMenu', id);
    showAlertMessage(t('CUSTOM_MENU.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CUSTOM_MENU.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCustomMenu(activeResponse.value.id);
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('CUSTOM_MENU.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('CUSTOM_MENU.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CUSTOM_MENU.HEADER')"
        :description="$t('CUSTOM_MENU.DESCRIPTION')"
        feature-name="custom_menus"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('CUSTOM_MENU.HEADER_BTN_TXT')"
            @click="openAddModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 ltr:pr-4 rtl:pl-4 font-semibold text-left text-n-slate-11"
          >
            <span class="mb-0">
              {{ thHeader }}
            </span>
          </th>
        </thead>

        <CustomMenuTableBody
          :menus="records"
          :loading="loading"
          @edit="openEditModal"
          @delete="openDeletePopup"
        />
      </table>
    </template>

    <woot-modal
      v-model:show="showCustomMenuModal"
      :on-close="hideCustomMenuModal"
    >
      <CustomMenuModal
        :mode="customMenuModalMode"
        :selected-menu="selectedMenu"
        @close="hideCustomMenuModal"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CUSTOM_MENU.DELETE.CONFIRM.TITLE')"
      :message="$t('CUSTOM_MENU.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
